import { HTMLInputTypeAttribute, forwardRef, HTMLAttributes } from 'react';
import { InputBoxInput } from './InputBox.Input';
import { InputBoxMaskedInput } from './InputBox.MaskedInput';
import clsx from 'clsx';
import { Mask } from './InputBox.masks';

export type ConnectedProps<T> = {
  label: string;
  value: T;
  onChange: (value: T) => void;
  error?: string;
};

export type InputBoxProps<T> = {
  name: string;
  placeholder?: string;
  type?: HTMLInputTypeAttribute;
  mask?: Mask;
  autoComplete?: string;
  disabled?: boolean;
  inputClassNames?: string;
  inputMode?: HTMLAttributes<HTMLLIElement>['inputMode'];
  maxLength?: number;
  hiddenBorder?: boolean;
  prefill?: string;
} & ConnectedProps<T>;

export type InputProps = {
  name: string;
  placeholder?: string;
  type?: HTMLInputTypeAttribute;
  inputMode?: HTMLAttributes<HTMLLIElement>['inputMode'];
  autoComplete?: string;
  mask?: Mask;
  disabled?: boolean;
  onChange: (e: any) => void;
  value?: string | number;
  invalid: boolean;
  errorId: string;
  className: string;
  maxLength?: number;
};

// eslint-disable-next-line react/display-name
export const InputBox = forwardRef<HTMLInputElement, InputBoxProps<string>>(
  (
    {
      onChange,
      value,
      name,
      type = 'text',
      inputMode,
      label,
      placeholder,
      autoComplete,
      error,
      mask,
      disabled,
      inputClassNames,
      maxLength,
      hiddenBorder = false,
      prefill,
    },
    ref,
  ) => {
    const errorId = `${name}-error`;
    const invalid = false;

    const styles = clsx(
      'focus:ring-1 focus:border-inputBorder ring-inputBorder  rounded-md block h-input w-full border p-2 border-gray-300 outline-none ring-0',
      inputClassNames,
      disabled
        ? 'text-disabled'
        : 'transition-[text-indent_background-color] hover:bg-ecru hover:indent-2 focus:border-0 focus:bg-ecru focus:indent-2 focus:outline-none focus:ring-0',
      error ? 'focus:outline-none !bg-error' : '',
    );

    return (
      <div>
        {label && (
          <label htmlFor={name} className="block">
            {label}
          </label>
        )}
        <div className={clsx('relative', label ? 'mt-1' : '')}>
          {prefill && (
            <p className="absolute bottom-0 z-10 mb-[2px] pb-4 md:mb-0">
              {prefill}
            </p>
          )}
          <div
            className={`${clsx(
              'border-b-line pb-[4px]',
              disabled ? 'border-b-disabled' : 'border-b-black',
              hiddenBorder && 'border-b-0',
            )}`}
          >
            {mask ? (
              <InputBoxMaskedInput
                type={type}
                inputMode={inputMode}
                ref={ref}
                name={name}
                errorId={errorId}
                placeholder={placeholder}
                autoComplete={autoComplete}
                invalid={invalid}
                value={value}
                onChange={(val) => onChange(val)}
                mask={mask}
                disabled={disabled}
                className={`${clsx(styles, prefill && 'relative pl-12')}`}
                maxLength={maxLength}
              />
            ) : (
              <InputBoxInput
                type={type}
                inputMode={inputMode}
                ref={ref}
                name={name}
                errorId={errorId}
                placeholder={placeholder}
                autoComplete={autoComplete}
                invalid={invalid}
                value={value}
                disabled={disabled}
                onChange={(e) => onChange(e.target.value)}
                className={`${clsx(styles, prefill && 'relative pl-12')}`}
                maxLength={maxLength}
              />
            )}
          </div>
        </div>
        {error && <p className="text-red-500">{error}</p>}
      </div>
    );
  },
);
